import type { AttackSkill } from '../../../battle/types';
import { BATTLE_PACE_SCALE } from '../../../data/constants';
import { playSfx } from '../../../render/utils/audio';
import type { SkillBundle } from '../../types';
import { Cannonball, FLIGHT_DURATION_MS } from './Cannonball';

const ROOT = '/assets/textures/characters/sparky_bot/skills/cannon';

// Frame in the cannon sheet at which the muzzle fires (smoke wisp visible).
// Default - overridable per character via timings.skills.cannon.impactFrame.
const FIRE_FRAME = 9;
// End of the recoil: a non-final multi-shot plays up to here, freezes, and
// the next shot rolls back to FIRE_FRAME. Overridable via impactFrameEnd.
const FIRE_FRAME_END = 11;
// pauseAtFrame hold long enough to outlive any between-shots gap; the next
// playFrames cancels it.
const MULTI_SHOT_HOLD_MS = 600000;
// Spawn / impact heights are FRACTIONS of bodyHeight measured from the feet.
// 0.5 = mid-body, 0.7 = chest area, 1.0 = top of head. Increase to raise.
const MUZZLE_HEIGHT_FRAC = 0.6;
const IMPACT_HEIGHT_FRAC = 0.6;
// Forward offset from attacker's center, as fraction of bodyWidth.
const MUZZLE_FORWARD_FRAC = 0.4;
// Nudge the BAM! burst this many design px past the impact point, toward the
// defender (attacker's facing direction), so it reads as landing ON them.
const BAM_IMPACT_PUSH = -30;
// BAM! burst tuning for a direct cannon hit (size in design px, speed in
// frames/tick). Independent from the arena wall-drop burst.
const BAM_SIZE = 155;
const BAM_SPEED = 0.9;

// Wall-time (ms) for a sprite anim to advance `frames` frames at `speed`
// frames/tick. The shared ticker runs at ~60Hz scaled by 1/BATTLE_PACE_SCALE
// (main.ts), so effective fps = 60 * speed / BATTLE_PACE_SCALE. Used to start
// the defender's dodge early enough that its peak frame lands as the ball passes.
function framesToWallMs(frames: number, speed: number): number {
  const fps = (60 * speed) / BATTLE_PACE_SCALE;
  return fps > 0 ? (frames / fps) * 1000 : 0;
}

const ENGINE: AttackSkill = {
  kind: 'attack',
  id: 'cannon',
  name: 'Cannon',
  damageMultiplier: 3,
  pierceBlock: false,
  animationId: 'cannon',
  reach: 'ranged', // projectile shot - only a ranged skill can counter it
  // No knock-up: a hit plays plain pain (multi-shot chains read better).
};

export const CANNON: SkillBundle = {
  engine: ENGINE,
  // Strong attack - duck the battle loop and hit a brass stinger over it.
  musicStinger: 'brass',
  assets: {
    sheets: {
      cannon: { url: `${ROOT}/cannon_v2.png`, cols: 4, rows: 4 },
    },
    textures: {
      cannonball: `${ROOT}/cannonball.png`,
      logIcon: `${ROOT}/ui-log-icon.png`,
      logCircleIcon: `${ROOT}/ui-log-circle-icon.png`,
    },
    sfx: {
      shot: `${ROOT}/cannonshot.mp3`,
      impact: `${ROOT}/impact.mp3`, // TODO Change this SFX
      ricochet: `${ROOT}/ricochet.mp3`,
      // Whoosh of the ball flying past a dodging defender. Lives in the
      // character's dodge folder (it is the defender's near-miss sound).
      passby: '/assets/textures/characters/sparky_bot/dodge/cannon-ball-passby.wav',
    },
  },
  async playAttack(ctx) {
    const { attacker, defender, scene, assets } = ctx;
    const cannonFrames = assets.sheets.cannon;
    const cannonballTex = assets.textures.cannonball;
    if (!cannonFrames || !cannonballTex) return;

    // Per-character overrides (speed / offset / fire frame) fall back to the
    // skill's built-in defaults.
    const t = attacker.timings.skills?.[ENGINE.id];
    const fireFrame = t?.impactFrame ?? FIRE_FRAME;
    const cannonSpeed = t?.speed ?? 1;

    // Cannon anim plays in place - no lunge. A multi-shot REPEAT skips the
    // windup (rolls back to the muzzle frame and fires again); every
    // NON-FINAL shot plays only up to the recoil end and freezes there,
    // waiting for the next roll-back. Only the final shot lets the full
    // recovery tail play, with the idle snap after it.
    const repeat = (ctx.shotIndex ?? 0) > 0;
    const lastShot = (ctx.shotIndex ?? 0) >= (ctx.shotCount ?? 1) - 1;
    const fireFrameEnd = t?.impactFrameEnd ?? FIRE_FRAME_END;
    attacker.playFrames(cannonFrames, {
      loop: false,
      speed: t?.speed,
      offsetX: t?.offsetX,
      offsetY: t?.offsetY,
      scaleMultiplier: t?.scale,
      ...(repeat ? { startFrame: fireFrame } : {}),
      ...(lastShot
        ? {}
        : { pauseAtFrame: { frame: fireFrameEnd, durationMs: MULTI_SHOT_HOLD_MS } }),
    });
    if (lastShot) attacker.scheduleIdleAfterCurrentAnim();

    const blocked = ctx.defenderReaction === 'block';
    const dodged = ctx.defenderReaction === 'dodge';

    // Defender's dodge: start it so its peak frame (dodge.impactFrame) lands
    // exactly when the ball flies past, which happens at muzzle-fire (the windup
    // to fireFrame) + the ball's flight time. The dodge plays at its OWN normal
    // speed and FULL length - we only shift WHEN it begins (it may need to start
    // during the windup, before the ball spawns, if its lead is long). The
    // returned promise resolves when the dodge finishes; we await it before
    // returning so later events (idle reset, next round) can't cut it short.
    let dodgeDone: Promise<void> | undefined;
    if (dodged && ctx.playDefenderDodge) {
      const dodgeT = defender.timings.dodge;
      const dodgeImpact = dodgeT?.impactFrame ?? 0;
      const dodgeSpeed = dodgeT?.speed ?? 1;
      const leadMs = framesToWallMs(dodgeImpact, dodgeSpeed);
      // A repeat starts right at the muzzle frame - no windup to wait out.
      const windupMs = repeat ? 0 : framesToWallMs(fireFrame, cannonSpeed);
      const startInMs = Math.max(0, windupMs + FLIGHT_DURATION_MS - leadMs);
      dodgeDone = ctx.playDefenderDodge(startInMs);
    }

    // Wait until the muzzle fires before spawning the projectile.
    await attacker.awaitFrame(fireFrame);

    // Boom. Master volume applied via playSfx helper.
    const shotSfx = assets.sfx.shot;
    if (shotSfx) playSfx(shotSfx);

    // Spawn at the muzzle (offset from attacker center, height from feet).
    const startX = attacker.x + attacker.bodyWidth * MUZZLE_FORWARD_FRAC * attacker.facing;
    const startY = attacker.y - attacker.bodyHeight * MUZZLE_HEIGHT_FRAC;
    const targetX = defender.x;
    const targetY = defender.y - defender.bodyHeight * IMPACT_HEIGHT_FRAC;

    // On dodge the ball passes through and keeps flying to the screen edge
    // (defender stepped aside), rendered BEHIND the fighter so it reads as a
    // near-miss. On hit/block it disposes at impact - the "ricochet into wall"
    // visual on block is owned by the arena (via playArenaEvent below) so the
    // embed appears to fly out of the impact point continuously.
    const ball = new Cannonball({
      texture: cannonballTex,
      startX,
      startY,
      targetX,
      targetY,
      passthrough: dodged,
    });
    scene.spawnEffect(ball, dodged);

    // Block until the projectile lands - renderer's damage event fires next
    // and applies the actual hit on the defender at this exact moment.
    await ball.flyAndImpact();

    // Outcome sound, mutually exclusive. Direct hit: heavy thud. Blocked:
    // metallic ping off armor. Dodged: whoosh of the ball flying past.
    const outcomeSfx = dodged
      ? assets.sfx.passby
      : blocked
        ? assets.sfx.ricochet
        : assets.sfx.impact;
    if (outcomeSfx) playSfx(outcomeSfx);

    // Direct hit: punch out a BAM! burst at the impact point (the ball's target
    // on the defender). Dodge passes through, block embeds in the wall - neither
    // lands on the defender, so no burst there.
    if (!dodged && !blocked) {
      scene.spawnBam(targetX + BAM_IMPACT_PUSH * attacker.facing, targetY, {
        size: BAM_SIZE,
        speed: BAM_SPEED,
      });
    }

    // On block: hand off to the arena's embed system synchronously so the
    // ball lodges in the wall as part of the same continuous motion. The
    // engine still emits an arena_attach event a few steps later; the arena's
    // playArenaEvent is idempotent so that second call no-ops.
    if (blocked) {
      const arena = ctx.scene.getArena();
      if (arena?.bundle.playArenaEvent) {
        await arena.bundle.playArenaEvent(
          {
            type: 'arena_attach',
            side: ctx.defenderActor,
            objectId: 'cannonball',
            sourceSkillId: 'cannon',
            fromPosition: { x: targetX, y: targetY },
          },
          { scene: ctx.scene, sheets: arena.sheets, textures: arena.textures },
        );
      }
    }

    // Let the dodge play all the way to its last frame before the attack ends.
    // Mid-burst the next shell fires over the still-playing dodge instead.
    if (dodgeDone && lastShot) await dodgeDone;
  },
};
